<template>
    <v-card class="tournament-share" flat :style="{ '--primary-color': primaryColor }" >
        <!-- Header -->
        <div class="share-header">
            <h2 class="share-title">{{ $t('shareTitle') }}</h2>
            <button class="close-btn" @click="$emit('closeWindow')">
                <v-icon size="20" class="icon-themed">fa-regular fa-times</v-icon>
            </button>
        </div>

        <!-- Tab Bar -->
        <div class="tab-bar">
            <button
                class="tab-option"
                :class="{ active: activeTab === 0 }"
                :style="activeTab === 0 ? activeTabStyle : {}"
                @click="activeTab = 0">
                <v-icon size="16" :color="activeTab === 0 ? '#fff' : ''">fa-regular fa-list-ul</v-icon>
                <span>{{ $t('rankingTab') || 'Ranking' }}</span>
            </button>
            <button
                class="tab-option"
                :class="{ active: activeTab === 1 }"
                :style="activeTab === 1 ? activeTabStyle : {}"
                @click="activeTab = 1">
                <v-icon size="16" :color="activeTab === 1 ? '#fff' : ''">fa-regular fa-user-plus</v-icon>
                <span>{{ $t('registrationTab') || 'Registration' }}</span>
            </button>
        </div>

        <!-- Subtitle -->
        <p class="share-subtitle">
            {{ activeTab === 0
                ? ($t('shareRankingSubtitle'))
                : ($t('shareRegistrationSubtitle'))
            }}
        </p>

        <!-- Scrollable content -->
        <div class="share-content">
            <!-- ============ RANKING TAB ============ -->
            <template v-if="activeTab === 0">
                <!-- Ranking Link Section -->
                <div class="accordion-section">
                    <div class="accordion-header" @click="toggleSection('rankingLink')">
                        <div class="accordion-header-left">
                            <span class="accordion-label">{{ $t('rankingLink') }}</span>
                            <v-icon size="18" class="icon-themed">fa-regular fa-link</v-icon>
                        </div>
                        <v-icon size="18" class="icon-themed" :class="{ 'rotate-180': expandedSections.rankingLink }">
                            fa-regular fa-chevron-down
                        </v-icon>
                    </div>
                    <div v-if="expandedSections.rankingLink" class="accordion-body">
                        <p class="webhook-desc">{{ $t('rankingLinkDesc') || 'Share this direct link so anyone can view the competition ranking.' }}</p>
                        <!-- Link Input -->
                        <div class="link-input-row">
                            <v-icon size="18" class="icon-themed mr-2">fa-regular fa-link</v-icon>
                            <span class="link-text">{{ shareableLink }}</span>
                            <button class="copy-btn" @click="copyToClipboard(shareableLink)">
                                <v-icon size="18" class="icon-themed">fa-regular fa-copy</v-icon>
                            </button>
                        </div>
                        <!-- Divider -->
                        <div class="section-divider"></div>
                        <!-- Social Media -->
                        <div class="social-row">
                            <span class="social-label">{{ $t('shareOnSocialMedia') }}</span>
                            <div class="social-icons">
                                <button class="social-circle" :style="socialCircleStyle" @click="copyToClipboard(shareableLink)">
                                    <v-icon size="16" :color="primaryColor">fa-regular fa-link</v-icon>
                                </button>
                                <button class="social-circle" :style="socialCircleStyle" @click="shareToTelegram">
                                    <v-icon size="16" :color="primaryColor">fa-brands fa-telegram</v-icon>
                                </button>
                                <button class="social-circle" :style="socialCircleStyle" @click="shareToWhatsApp">
                                    <v-icon size="16" :color="primaryColor">fa-brands fa-whatsapp</v-icon>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Ranking Embed Code Section -->
                <div class="accordion-section">
                    <div class="accordion-header" @click="toggleSection('rankingEmbed')">
                        <div class="accordion-header-left">
                            <span class="accordion-label">{{ $t('rankingEmbedCode') || 'Ranking Embed Code' }}</span>
                            <v-icon size="18" class="icon-themed">fa-regular fa-code</v-icon>
                        </div>
                        <v-icon size="18" class="icon-themed" :class="{ 'rotate-180': expandedSections.rankingEmbed }">
                            fa-regular fa-chevron-down
                        </v-icon>
                    </div>
                    <div v-if="expandedSections.rankingEmbed" class="accordion-body">
                        <p class="webhook-desc">{{ $t('rankingEmbedDesc') || 'Embed the ranking table directly into your website using an iframe.' }}</p>
                        <!-- Embed Code Display -->
                        <div class="embed-code-box">
                            <pre class="embed-code-text">{{ embedCode }}</pre>
                            <button class="copy-btn" @click="copyToClipboard(embedCode)">
                                <v-icon size="18" class="icon-themed">fa-regular fa-copy</v-icon>
                            </button>
                        </div>
                        <!-- Divider -->
                        <div class="section-divider"></div>
                        <!-- Embed Configuration -->
                        <div class="embed-config-toggle" @click="toggleSection('embedConfig')">
                            <div class="accordion-header-left">
                                <span class="accordion-label">{{ $t('embedConfiguration') || 'Embed Configuration' }}</span>
                                <v-icon size="18" class="icon-themed">fa-regular fa-cog</v-icon>
                            </div>
                            <v-icon size="18" class="icon-themed" :class="{ 'rotate-180': expandedSections.embedConfig }">
                                fa-regular fa-chevron-down
                            </v-icon>
                        </div>
                        <div v-if="expandedSections.embedConfig" class="embed-config-content">
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-select
                                        v-model="embedConfig.theme"
                                        :items="[
                                            { text: $t('lightTheme') || 'Light Theme', value: 'light' },
                                            { text: $t('darkTheme') || 'Dark Theme', value: 'dark' }
                                        ]"
                                        :label="$t('theme') || 'Theme'"
                                        outlined dense hide-details class="mb-3">
                                    </v-select>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-select
                                        v-model="embedConfig.lang"
                                        :items="[
                                            { text: $t('autoDetect') || 'Auto-detect', value: '' },
                                            ...availableLanguages.map(lang => ({ text: lang.name, value: lang.code }))
                                        ]"
                                        :label="$t('defaultLanguage') || 'Default Language'"
                                        outlined dense hide-details class="mb-3">
                                    </v-select>
                                </v-col>
                            </v-row>
                            <!-- Allowed Languages -->
                            <div class="allowed-langs-section mt-3">
                                <span class="field-label">{{ $t('allowedLanguages') || 'Allowed Languages' }}</span>
                                <p class="allowed-langs-desc">{{ $t('allowedLanguagesDescription') || 'Select which languages users can choose from. Leave empty to allow all.' }}</p>
                                <div class="language-chips">
                                    <v-chip
                                        v-for="lang in availableLanguages"
                                        :key="'rank-' + lang.code"
                                        :color="embedConfig.allowedLangs.includes(lang.code) ? primaryColor : 'default'"
                                        :text-color="embedConfig.allowedLangs.includes(lang.code) ? '#fff' : '#3b3b3b'"
                                        :outlined="!embedConfig.allowedLangs.includes(lang.code)"
                                        small
                                        class="lang-chip"
                                        @click="toggleAllowedLang(lang.code)">
                                        {{ lang.name }}
                                        <v-icon v-if="embedConfig.allowedLangs.includes(lang.code)" small right color="#fff">fa-regular fa-check</v-icon>
                                    </v-chip>
                                </div>
                                <div class="lang-actions mt-2">
                                    <button class="lang-action-btn" @click="embedConfig.allowedLangs = availableLanguages.map(l => l.code)">
                                        {{ $t('selectAll') || 'Select All' }}
                                    </button>
                                    <button class="lang-action-btn" @click="embedConfig.allowedLangs = []">
                                        {{ $t('clearAll') || 'Clear All' }}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <!-- Divider -->
                        <div class="section-divider"></div>
                        <!-- Send to Developer -->
                        <div class="developer-section">
                            <div class="developer-info">
                                <span class="developer-title">{{ $t('sendToYourDeveloper') || 'Send to your Developer' }}</span>
                                <span class="developer-desc">{{ $t('developerSectionDescription') || 'Send both embed codes directly to your developer via email.' }}</span>
                            </div>
                            <div class="developer-input-row">
                                <div class="developer-input-wrapper">
                                    <input
                                        v-model="developerEmail"
                                        type="email"
                                        :placeholder="$t('developerEmail') || 'developer@example.com'"
                                        class="developer-input"
                                    />
                                </div>
                                <button
                                    class="send-btn"
                                    :style="sendBtnStyle"
                                    :disabled="!isValidEmail || sending"
                                    @click="sendEmbedCodeByEmail('ranking')">
                                    <v-icon size="18" color="#fff">fa-regular fa-paper-plane</v-icon>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <!-- ============ REGISTER TAB ============ -->
            <template v-if="activeTab === 1">
                <!-- Registration Link Section -->
                <div class="accordion-section">
                    <div class="accordion-header" @click="toggleSection('regLink')">
                        <div class="accordion-header-left">
                            <span class="accordion-label">{{ $t('registrationLink') || 'Registration Link' }}</span>
                            <v-icon size="18" class="icon-themed">fa-regular fa-link</v-icon>
                        </div>
                        <v-icon size="18" class="icon-themed" :class="{ 'rotate-180': expandedSections.regLink }">
                            fa-regular fa-chevron-down
                        </v-icon>
                    </div>
                    <div v-if="expandedSections.regLink" class="accordion-body">
                        <p class="webhook-desc">{{ $t('regLinkDesc') || 'Share this direct link so users can register for the competition.' }}</p>
                        <div class="link-input-row">
                            <v-icon size="18" class="icon-themed mr-2">fa-regular fa-link</v-icon>
                            <span class="link-text">{{ registerLink }}</span>
                            <button class="copy-btn" @click="copyToClipboard(registerLink)">
                                <v-icon size="18" class="icon-themed">fa-regular fa-copy</v-icon>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Registration Embed Code Section -->
                <div class="accordion-section">
                    <div class="accordion-header" @click="toggleSection('regEmbed')">
                        <div class="accordion-header-left">
                            <span class="accordion-label">{{ $t('registrationEmbedCode') || 'Registration Embed Code' }}</span>
                            <v-icon size="18" class="icon-themed">fa-regular fa-code</v-icon>
                        </div>
                        <v-icon size="18" class="icon-themed" :class="{ 'rotate-180': expandedSections.regEmbed }">
                            fa-regular fa-chevron-down
                        </v-icon>
                    </div>
                    <div v-if="expandedSections.regEmbed" class="accordion-body">
                        <p class="webhook-desc">{{ $t('regEmbedDesc') || 'Embed the registration form directly into your website using an iframe.' }}</p>
                        <div class="embed-code-box">
                            <pre class="embed-code-text">{{ registerEmbedCode }}</pre>
                            <button class="copy-btn" @click="copyToClipboard(registerEmbedCode)">
                                <v-icon size="18" class="icon-themed">fa-regular fa-copy</v-icon>
                            </button>
                        </div>
                        <!-- Divider -->
                        <div class="section-divider"></div>
                        <!-- Embed Configuration -->
                        <div class="embed-config-toggle" @click="toggleSection('regEmbedConfig')">
                            <div class="accordion-header-left">
                                <span class="accordion-label">{{ $t('embedConfiguration') || 'Embed Configuration' }}</span>
                                <v-icon size="18" class="icon-themed">fa-regular fa-cog</v-icon>
                            </div>
                            <v-icon size="18" class="icon-themed" :class="{ 'rotate-180': expandedSections.regEmbedConfig }">
                                fa-regular fa-chevron-down
                            </v-icon>
                        </div>
                        <div v-if="expandedSections.regEmbedConfig" class="embed-config-content">
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-select
                                        v-model="embedConfig.theme"
                                        :items="[
                                            { text: $t('lightTheme') || 'Light Theme', value: 'light' },
                                            { text: $t('darkTheme') || 'Dark Theme', value: 'dark' }
                                        ]"
                                        :label="$t('theme') || 'Theme'"
                                        outlined dense hide-details class="mb-3">
                                    </v-select>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-select
                                        v-model="embedConfig.lang"
                                        :items="[
                                            { text: $t('autoDetect') || 'Auto-detect', value: '' },
                                            ...availableLanguages.map(lang => ({ text: lang.name, value: lang.code }))
                                        ]"
                                        :label="$t('defaultLanguage') || 'Default Language'"
                                        outlined dense hide-details class="mb-3">
                                    </v-select>
                                </v-col>
                            </v-row>
                            <!-- Allowed Languages -->
                            <div class="allowed-langs-section mt-3">
                                <span class="field-label">{{ $t('allowedLanguages') || 'Allowed Languages' }}</span>
                                <p class="allowed-langs-desc">{{ $t('allowedLanguagesDescription') || 'Select which languages users can choose from. Leave empty to allow all.' }}</p>
                                <div class="language-chips">
                                    <v-chip
                                        v-for="lang in availableLanguages"
                                        :key="'reg-' + lang.code"
                                        :color="embedConfig.allowedLangs.includes(lang.code) ? primaryColor : 'default'"
                                        :text-color="embedConfig.allowedLangs.includes(lang.code) ? '#fff' : '#3b3b3b'"
                                        :outlined="!embedConfig.allowedLangs.includes(lang.code)"
                                        small
                                        class="lang-chip"
                                        @click="toggleAllowedLang(lang.code)">
                                        {{ lang.name }}
                                        <v-icon v-if="embedConfig.allowedLangs.includes(lang.code)" small right color="#fff">fa-regular fa-check</v-icon>
                                    </v-chip>
                                </div>
                                <div class="lang-actions mt-2">
                                    <button class="lang-action-btn" @click="embedConfig.allowedLangs = availableLanguages.map(l => l.code)">
                                        {{ $t('selectAll') || 'Select All' }}
                                    </button>
                                    <button class="lang-action-btn" @click="embedConfig.allowedLangs = []">
                                        {{ $t('clearAll') || 'Clear All' }}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <!-- Divider -->
                        <div class="section-divider"></div>
                        <!-- Send to Developer -->
                        <div class="developer-section">
                            <div class="developer-info">
                                <span class="developer-title">{{ $t('sendToYourDeveloper') || 'Send to your Developer' }}</span>
                                <span class="developer-desc">{{ $t('developerSectionDescription') || 'Send both embed codes directly to your developer via email.' }}</span>
                            </div>
                            <div class="developer-input-row">
                                <div class="developer-input-wrapper">
                                    <input
                                        v-model="developerEmail"
                                        type="email"
                                        :placeholder="$t('developerEmail') || 'developer@example.com'"
                                        class="developer-input"
                                    />
                                </div>
                                <button
                                    class="send-btn"
                                    :style="sendBtnStyle"
                                    :disabled="!isValidEmail || sending"
                                    @click="sendEmbedCodeByEmail('registration')">
                                    <v-icon size="18" color="#fff">fa-regular fa-paper-plane</v-icon>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Registration via Webhook Section -->
                <div class="accordion-section">
                    <div class="accordion-header" @click="toggleSection('webhook')">
                        <div class="accordion-header-left">
                            <span class="accordion-label">{{ $t('registrationViaWebhook') || 'Registration via Elementor Webhook' }}</span>
                            <v-icon size="18" class="icon-themed">fa-brands fa-wordpress</v-icon>
                        </div>
                        <v-icon size="18" class="icon-themed" :class="{ 'rotate-180': expandedSections.webhook }">
                            fa-regular fa-chevron-down
                        </v-icon>
                    </div>
                    <div v-if="expandedSections.webhook" class="accordion-body">
                        <!-- Loading state -->
                        <div v-if="adminInfoLoading" class="webhook-loading">
                            <v-progress-circular indeterminate size="24" :color="primaryColor"></v-progress-circular>
                        </div>
                        <!-- Unavailable state (no partnerId) -->
                        <div v-else-if="!adminInfo || !adminInfo.partnerId" class="webhook-unavailable">
                            <v-icon size="40" class="icon-themed mb-2">fa-regular fa-link-slash</v-icon>
                            <span class="unavailable-title">{{ $t('webhookUnavailable') || 'Webhook integration is not available' }}</span>
                            <span class="unavailable-desc">{{ $t('webhookUnavailableDesc') || 'Partner configuration is required to enable webhook registration. Please contact support.' }}</span>
                        </div>
                        <!-- Webhook content -->
                        <template v-else>
                            <p class="webhook-desc">
                                {{ $t('webhookDescription') || 'If your website is built with WordPress or Elementor, use the following URL in your form\'s Webhook configuration.' }}
                            </p>
                            <div class="webhook-field">
                                <span class="field-label">{{ $t('webhookUrl') || 'Webhook URL' }}</span>
                                <div class="link-input-row">
                                    <v-icon size="18" class="icon-themed mr-2">fa-regular fa-link</v-icon>
                                    <span class="link-text">{{ webhookUrl }}</span>
                                    <button class="copy-btn" @click="copyToClipboard(webhookUrl)">
                                        <v-icon size="18" class="icon-themed">fa-regular fa-copy</v-icon>
                                    </button>
                                </div>
                            </div>
                            <div class="webhook-field mt-3">
                                <span class="field-label">{{ $t('webhookFieldIds') || 'Make sure your form inputs use the following field IDs:' }}</span>
                                <div class="webhook-fields-grid">
                                    <div class="webhook-field-card">
                                        <span class="field-card-label">{{ $t('webhookFieldFirstName') || 'For Name Input' }}</span>
                                        <span class="field-card-id">ID: <code>first_name</code></span>
                                    </div>
                                    <div class="webhook-field-card">
                                        <span class="field-card-label">{{ $t('webhookFieldLastName') || 'For Last Name Input' }}</span>
                                        <span class="field-card-id">ID: <code>last_name</code></span>
                                    </div>
                                    <div class="webhook-field-card">
                                        <span class="field-card-label">{{ $t('webhookFieldPhone') || 'For Phone Input' }}</span>
                                        <span class="field-card-id">ID: <code>phone</code></span>
                                    </div>
                                    <div class="webhook-field-card">
                                        <span class="field-card-label">{{ $t('webhookFieldEmail') || 'For Email Input' }}</span>
                                        <span class="field-card-id">ID: <code>email</code></span>
                                    </div>
                                </div>
                            </div>
                            <!-- Divider -->
                            <div class="section-divider"></div>
                            <!-- Send to Developer -->
                            <div class="developer-section">
                                <div class="developer-info">
                                    <span class="developer-title">{{ $t('sendToYourDeveloper') || 'Send to your Developer' }}</span>
                                    <span class="developer-desc">{{ $t('webhookDeveloperDesc') || 'Send the webhook configuration to your developer via email.' }}</span>
                                </div>
                                <div class="developer-input-row">
                                    <div class="developer-input-wrapper">
                                        <input
                                            v-model="webhookDeveloperEmail"
                                            type="email"
                                            :placeholder="$t('developerEmail') || 'developer@example.com'"
                                            class="developer-input"
                                        />
                                    </div>
                                    <button
                                        class="send-btn"
                                        :style="sendBtnStyle"
                                        :disabled="!isValidWebhookEmail || sending"
                                        @click="sendWebhookInfoByEmail">
                                        <v-icon size="18" color="#fff">fa-regular fa-paper-plane</v-icon>
                                    </button>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>

                <!-- Registration via API Section -->
                <div class="accordion-section">
                    <div class="accordion-header" @click="toggleSection('api')">
                        <div class="accordion-header-left">
                            <span class="accordion-label">{{ $t('registrationViaApi') || 'Registration via API' }}</span>
                            <v-icon size="18" class="icon-themed">fa-regular fa-cog</v-icon>
                        </div>
                        <v-icon size="18" class="icon-themed" :class="{ 'rotate-180': expandedSections.api }">
                            fa-regular fa-chevron-down
                        </v-icon>
                    </div>
                    <div v-if="expandedSections.api" class="accordion-body">
                        <!-- Loading state -->
                        <div v-if="adminInfoLoading" class="webhook-loading">
                            <v-progress-circular indeterminate size="24" :color="primaryColor"></v-progress-circular>
                        </div>
                        <!-- Unavailable state (no apiKey) -->
                        <div v-else-if="!adminInfo || !adminInfo.apiKey || adminInfo.apiKey.length === 0" class="webhook-unavailable">
                            <v-icon size="40" class="icon-themed mb-2">fa-regular fa-key</v-icon>
                            <span class="unavailable-title">{{ $t('apiUnavailable') || 'API integration is not available' }}</span>
                            <span class="unavailable-desc">{{ $t('apiUnavailableDesc') || 'An API key is required to enable API registration. Please contact support.' }}</span>
                        </div>
                        <template v-else>
                        <p class="webhook-desc">
                            {{ $t('apiDescription') || 'Use this secure API endpoint to programmatically register participants. Each request registers one participant.' }}
                        </p>
                        <div class="webhook-field">
                            <span class="field-label">{{ $t('apiEndpoint') || 'API Endpoint' }}</span>
                            <div class="link-input-row">
                                <span class="method-badge">POST</span>
                                <span class="link-text">{{ apiEndpoint }}</span>
                                <button class="copy-btn" @click="copyToClipboard(apiEndpoint)">
                                    <v-icon size="18" class="icon-themed">fa-regular fa-copy</v-icon>
                                </button>
                            </div>
                        </div>
                        <div class="webhook-field mt-3">
                            <span class="field-label">{{ $t('apiKey') || 'API Key' }}</span>
                            <div class="link-input-row">
                                <v-icon size="18" class="icon-themed mr-2">fa-regular fa-key</v-icon>
                                <span class="link-text api-key-text">{{ showApiKey ? actualApiKey : maskedApiKey }}</span>
                                <button class="copy-btn mr-1" @click="showApiKey = !showApiKey">
                                    <v-icon size="18" class="icon-themed">fa-regular {{ showApiKey ? 'fa-eye-slash' : 'fa-eye' }}</v-icon>
                                </button>
                                <button class="copy-btn" @click="copyToClipboard(actualApiKey)">
                                    <v-icon size="18" class="icon-themed">fa-regular fa-copy</v-icon>
                                </button>
                            </div>
                        </div>
                        <div class="webhook-field mt-3">
                            <span class="field-label">{{ $t('requestBody') || 'Request Body (JSON)' }}</span>
                            <div class="embed-code-box">
                                <pre class="embed-code-text">{{ apiRequestBody }}</pre>
                                <button class="copy-btn" @click="copyToClipboard(apiRequestBody)">
                                    <v-icon size="18" class="icon-themed">fa-regular fa-copy</v-icon>
                                </button>
                            </div>
                        </div>
                        <div class="webhook-field mt-3">
                            <span class="field-label">{{ $t('requiredHeaders') || 'Required Headers' }}</span>
                            <div class="required-fields-list">
                                <code class="field-chip">Content-Type: application/json</code>
                                <code class="field-chip">api-key: &lt;YOUR_PARTNER_API_KEY_HERE&gt;</code>
                            </div>
                        </div>
                        </template>
                    </div>
                </div>
            </template>
        </div>
    </v-card>
</template>

<script>
import SharingRepository from '../../../repositories/sharing';
import TournamentRepository from '../../../repositories/tournament';

export default {
    name: 'TournamentShare',
    props: {
        tournamentId: {
            type: Number,
            required: true
        },
        externalId: {
            type: String,
            required: true
        },
        tournamentStatus: {
            type: String,
            required: false
        }
    },
    data() {
        return {
            activeTab: 0,
            developerEmail: '',
            webhookDeveloperEmail: '',
            sending: false,
            showApiKey: false,
            adminInfo: null,
            adminInfoLoading: false,
            expandedSections: {
                rankingLink: false,
                rankingEmbed: false,
                regLink: false,
                regEmbed: false,
                webhook: false,
                api: false,
                embedConfig: false,
                regEmbedConfig: false
            },
            embedConfig: {
                theme: 'light',
                lang: '',
                allowedLangs: []
            },
            availableLanguages: [
                { code: 'en', name: 'English' },
                { code: 'es', name: 'Espanol' },
                { code: 'ko', name: 'Korean' },
                { code: 'th', name: 'Thai' },
                { code: 'pt', name: 'Portuguese' },
                { code: 'zh', name: 'Chinese (Simplified)' },
                { code: 'zh-Hant', name: 'Chinese (Traditional)' },
                { code: 'vi', name: 'Vietnamese' },
                { code: 'ja', name: 'Japanese' }
            ]
        };
    },
    computed: {
        primaryColor() {
            const colors = this.$store.state.tournament?.tournamentSettings?.colors;
            if (colors && colors.primary) {
                const c = colors.primary.replace('#', '');
                return `#${c}`;
            }
            const clubColor = this.$store.state.clubMember?.club?.primaryColor;
            if (clubColor) {
                return `#${clubColor.replace('#', '')}`;
            }
            return '#d00808';
        },
        activeTabStyle() {
            return {
                backgroundColor: this.primaryColor,
                color: '#ffffff'
            };
        },
        socialCircleStyle() {
            return {
                borderColor: this.primaryColor
            };
        },
        sendBtnStyle() {
            return {
                backgroundColor: this.primaryColor
            };
        },
        shareableLink() {
            const params = this.buildUrlParameters();
            const queryString = params ? `&${params}` : '';
            return `https://app.swiset.com/tournaments/embed-rank?tournamentId=${this.externalId}${queryString}`;
        },
        embedCode() {
            return `<iframe \n  src="${this.shareableLink}" \n  width="100%" \n  height="800" \n  frameborder="0" \n  title="Competition Ranking"></iframe>`;
        },
        registerLink() {
            const params = this.buildUrlParameters();
            const queryString = params ? `&${params}` : '';
            return `https://app.swiset.com/tournaments/embed-register?tournamentId=${this.externalId}-${this.tournamentId}${queryString}`;
        },
        registerEmbedCode() {
            return `<iframe \n  src="${this.registerLink}" \n  width="100%" \n  height="900" \n  frameborder="0" \n  title="Competition Registration"></iframe>`;
        },
        shareMessage() {
            return this.$t('shareMessage', { tournamentName: this.$store.state.tournament?.tournamentName || 'Tournament' });
        },
        isValidEmail() {
            return this.developerEmail && /.+@.+\..+/.test(this.developerEmail);
        },
        clubId() {
            return this.$store.state.clubMember?.club?.id || null;
        },
        userLanguage() {
            return this.$store.state.preferredLanguage || this.$i18n.locale || 'en';
        },
        webhookUrl() {
            if (!this.adminInfo || !this.adminInfo.partnerId) return '';
            return `https://api.swiset.com/api/ext/tournaments/${this.tournamentId}/partners/${this.adminInfo.partnerId}/pre-registrations`;
        },
        isValidWebhookEmail() {
            return this.webhookDeveloperEmail && /.+@.+\..+/.test(this.webhookDeveloperEmail);
        },
        apiEndpoint() {
            return `https://api.swiset.com/api-key/tournaments/${this.tournamentId}/registrations`;
        },
        actualApiKey() {
            return this.adminInfo?.apiKey || '';
        },
        maskedApiKey() {
            const key = this.actualApiKey;
            if (!key) return '••••••••••••••••••••';
            return key.substring(0, 8) + '••••••••••••••••';
        },
        apiRequestBody() {
            return JSON.stringify({
                firstName: "John",
                lastName: "Doe",
                email: "participant@example.com",
                country: "US (Optional)",
                phoneNumber: "+1234567890 (Optional)",
                langKey: "en (Optional)"
            }, null, 2);
        }
    },
    mounted() {
        this.fetchAdminInfo();
    },
    methods: {
        async fetchAdminInfo() {
            this.adminInfoLoading = true;
            try {
                const response = await TournamentRepository.getAdminInfo(this.tournamentId);
                if (response.data) {
                    this.adminInfo = response.data;
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error fetching admin info:', error);
                this.adminInfo = null;
            } finally {
                this.adminInfoLoading = false;
            }
        },
        toggleSection(section) {
            this.expandedSections[section] = !this.expandedSections[section];
        },
        toggleAllowedLang(code) {
            const idx = this.embedConfig.allowedLangs.indexOf(code);
            if (idx > -1) {
                this.embedConfig.allowedLangs.splice(idx, 1);
            } else {
                this.embedConfig.allowedLangs.push(code);
            }
        },
        copyToClipboard(text) {
            navigator.clipboard.writeText(text).then(() => {
                this.$emit('copied');
            }).catch(err => {
                //eslint-disable-next-line
                console.error('Could not copy text: ', err);
            });
        },
        shareToWhatsApp() {
            const url = `https://wa.me/?text=${encodeURIComponent(this.shareMessage + ' ' + this.shareableLink)}`;
            window.open(url, '_blank');
        },
        shareToTelegram() {
            const url = `https://t.me/share/url?url=${encodeURIComponent(this.shareableLink)}&text=${encodeURIComponent(this.shareMessage)}`;
            window.open(url, '_blank');
        },
        buildUrlParameters() {
            const params = new URLSearchParams();
            if (this.embedConfig.theme && this.embedConfig.theme !== 'light') {
                params.append('theme', this.embedConfig.theme);
            }
            if (this.embedConfig.lang && this.embedConfig.lang.trim()) {
                params.append('lang', this.embedConfig.lang);
            }
            if (this.embedConfig.allowedLangs && this.embedConfig.allowedLangs.length > 0) {
                params.append('allowed_langs', this.embedConfig.allowedLangs.join(','));
            }
            return params.toString();
        },
        async sendWebhookInfoByEmail() {
            if (!this.isValidWebhookEmail) return;
            this.sending = true;
            try {
                const emailData = {
                    developerEmail: this.webhookDeveloperEmail,
                    langKey: this.userLanguage,
                    type: 'WEBHOOK',
                    webhookUrl: this.webhookUrl,
                    fields: ['first_name', 'last_name', 'phone', 'email']
                };
                if (this.clubId) {
                    emailData.club_id = this.clubId;
                }
                const response = await SharingRepository.shareCompetition(emailData);
                if (response.status < 400) {
                    this.$emit('emailSent', this.webhookDeveloperEmail);
                    this.webhookDeveloperEmail = '';
                } else {
                    throw new Error('Failed to send email');
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error sending webhook email:', error);
                this.$emit('emailError', error?.message || this.$t('emailSendFailed'));
            } finally {
                this.sending = false;
            }
        },
        async sendEmbedCodeByEmail(type) {
            if (!this.isValidEmail) return;
            this.sending = true;
            try {
                const emailData = {
                    developerEmail: this.developerEmail,
                    langKey: this.userLanguage
                };
                if (this.clubId) {
                    emailData.club_id = this.clubId;
                }
                if (type === 'ranking') {
                    emailData.type = 'EMBED';
                    emailData.iframeCode = this.embedCode;
                } else {
                    emailData.type = 'REGISTER';
                    emailData.registrationLink = this.registerLink;
                    emailData.iframeCode = this.registerEmbedCode;
                }
                const response = await SharingRepository.shareCompetition(emailData);
                if (response.status < 400) {
                    this.$emit('emailSent', this.developerEmail);
                    this.developerEmail = '';
                } else {
                    throw new Error('Failed to send email');
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error sending email:', error);
                this.$emit('emailError', error?.message || this.$t('emailSendFailed'));
            } finally {
                this.sending = false;
            }
        }
    }
}
</script>

<style lang="scss" scoped>
// Theme-aware color variables
.tournament-share {
    --share-bg: #f8f8f8;
    --share-text: #3b3b3b;
    --share-title-color: #14161b;
    --share-border: #ababab;
    --share-input-border: #3b3b3b;
    --share-tab-bg: #ebebeb;
    --share-chip-bg: #ebebeb;
    --share-muted: #666;
    --share-hover: rgba(0, 0, 0, 0.02);
    --share-icon-color: #333;

    background-color: var(--share-bg) !important;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 24px;
    border-radius: 0 !important;
    overflow: hidden;
    color: var(--share-text);
}

.share-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 20px;
    border-bottom: 1px solid var(--share-border);
    margin-bottom: 20px;
}

.share-title {
    font-family: 'Aeonik', 'Avenir', sans-serif;
    font-weight: 700;
    font-size: 30px;
    line-height: 40px;
    color: var(--share-title-color);
    margin: 0;
}

.close-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.tab-bar {
    display: flex;
    background-color: var(--share-tab-bg);
    border-radius: 10px;
    height: 40px;
    gap: 5px;
    margin-bottom: 16px;
    flex-shrink: 0;
}

.tab-option {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border: none;
    border-radius: 10px;
    background: transparent;
    cursor: pointer;
    font-family: 'Avenir', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: var(--share-text);
    transition: all 0.2s ease;
    padding: 0 10px;

    &.active {
        color: #ffffff;

        .v-icon {
            color: #ffffff !important;
        }
    }
}

.share-subtitle {
    font-family: 'Aeonik', 'Avenir', sans-serif;
    font-weight: 500;
    font-size: 18px;
    line-height: 1.5;
    color: var(--share-text);
    margin: 0 0 16px 0;
}

.share-content {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 20px;

    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: var(--share-tab-bg);
        border-radius: 16px;
    }

    &::-webkit-scrollbar-thumb {
        background: var(--primary-color, #c03131);
        border-radius: 16px;
        min-height: 60px;
    }

    scrollbar-width: thin;
}

// Icons that should follow theme
::v-deep .icon-themed {
    color: var(--share-icon-color) !important;
}

// Accordion sections
.accordion-section {
    border: 1px solid var(--share-border);
    border-radius: 8px;
    overflow: hidden;
    flex-shrink: 0;
}

.accordion-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 12px;
    cursor: pointer;
    user-select: none;

    &:hover {
        background-color: var(--share-hover);
    }
}

.accordion-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.accordion-label {
    font-family: 'Avenir', sans-serif;
    font-weight: 800;
    font-size: 14px;
    line-height: 1.5;
    color: var(--share-text);
}

.rotate-180 {
    transform: rotate(180deg);
    transition: transform 0.2s ease;
}

.accordion-body {
    padding: 0 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

// Link input row
.link-input-row {
    display: flex;
    align-items: center;
    border: 1px solid var(--share-input-border);
    border-radius: 4px;
    padding: 8px 16px;
    height: 35px;
    gap: 8px;
}

.link-text {
    flex: 1;
    font-family: 'Avenir', sans-serif;
    font-size: 16px;
    line-height: 1.5;
    color: var(--share-text);
    white-space: nowrap;
    overflow-x: auto;
    min-width: 0;
    user-select: text;
    cursor: text;

    &::-webkit-scrollbar {
        display: none;
    }

    -ms-overflow-style: none;
    scrollbar-width: none;
}

.copy-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &:hover {
        opacity: 0.7;
    }
}

// Section divider
.section-divider {
    height: 1px;
    background-color: var(--share-border);
    width: 100%;
}

// Social media row
.social-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.social-label {
    font-family: 'Avenir', sans-serif;
    font-weight: 800;
    font-size: 16px;
    line-height: 1.5;
    color: var(--share-text);
}

.social-icons {
    display: flex;
    gap: 8px;
}

.social-circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 1.5px solid;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
        opacity: 0.7;
    }
}

// Embed code box
.embed-code-box {
    display: flex;
    align-items: flex-start;
    border: 1px solid var(--share-input-border);
    border-radius: 4px;
    padding: 8px 16px;
    gap: 8px;
}

.embed-code-text {
    flex: 1;
    font-family: 'Avenir', monospace;
    font-size: 16px;
    line-height: 1.5;
    color: var(--share-text);
    white-space: pre-wrap;
    word-break: break-all;
    margin: 0;
    min-width: 0;
}

// Embed config toggle
.embed-config-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid var(--share-border);
    border-radius: 4px;
    padding: 12px;
    cursor: pointer;
    user-select: none;

    &:hover {
        background-color: var(--share-hover);
    }
}

.embed-config-content {
    padding: 12px 0 0;
}

// Developer section
.developer-section {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.developer-info {
    display: flex;
    flex-direction: column;
}

.developer-title {
    font-family: 'Avenir', sans-serif;
    font-weight: 800;
    font-size: 16px;
    line-height: 1.5;
    color: var(--share-text);
}

.developer-desc {
    font-family: 'Avenir', sans-serif;
    font-weight: 300;
    font-size: 14px;
    line-height: 1.5;
    color: var(--share-text);
}

.developer-input-row {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-top: 4px;
}

.developer-input-wrapper {
    flex: 1;
}

.developer-input {
    width: 100%;
    height: 35px;
    border: 1px solid var(--share-input-border);
    border-radius: 4px;
    padding: 8px 16px;
    font-family: 'Avenir', sans-serif;
    font-size: 16px;
    line-height: 1.5;
    color: var(--share-text);
    outline: none;
    background: transparent;

    &::placeholder {
        color: var(--share-border);
    }

    &:focus {
        border-color: var(--share-title-color);
    }
}

.send-btn {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: opacity 0.2s;

    &:hover:not(:disabled) {
        opacity: 0.85;
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
}

// Allowed languages
.allowed-langs-section {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.allowed-langs-desc {
    font-family: 'Avenir', sans-serif;
    font-size: 12px;
    color: var(--share-muted);
    line-height: 1.4;
    margin: 0 0 8px 0;
}

.language-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.lang-chip {
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
}

.lang-actions {
    display: flex;
    gap: 8px;
}

.lang-action-btn {
    font-family: 'Avenir', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: var(--share-text);
    background: none;
    border: 1px solid var(--share-border);
    border-radius: 4px;
    padding: 4px 12px;
    cursor: pointer;
    transition: background-color 0.15s;

    &:hover {
        background-color: var(--share-tab-bg);
    }
}

// Webhook / API section styles
.webhook-desc {
    font-family: 'Avenir', sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: var(--share-text);
    margin: 0;
}

.webhook-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.field-label {
    font-family: 'Avenir', sans-serif;
    font-weight: 800;
    font-size: 13px;
    line-height: 1.5;
    color: var(--share-text);
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.required-fields-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.field-chip {
    display: inline-block;
    background-color: var(--share-chip-bg);
    border-radius: 4px;
    padding: 2px 10px;
    font-family: 'Courier New', monospace;
    font-size: 13px;
    color: var(--share-text);
}

.method-badge {
    font-family: 'Aeonik', monospace;
    font-weight: 700;
    font-size: 12px;
    color: #ffffff;
    background-color: #4caf50;
    border-radius: 3px;
    padding: 2px 8px;
    flex-shrink: 0;
    margin-right: 8px;
}

.api-key-text {
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

// Webhook fields grid (2-column layout matching Figma)
.webhook-fields-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 4px;
}

.webhook-field-card {
    display: flex;
    flex-direction: column;
    gap: 2px;
    background-color: var(--share-chip-bg);
    border-radius: 6px;
    padding: 10px 12px;
}

.field-card-label {
    font-family: 'Avenir', sans-serif;
    font-size: 12px;
    color: var(--share-muted);
    line-height: 1.4;
}

.field-card-id {
    font-family: 'Avenir', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: var(--share-text);
    line-height: 1.4;

    code {
        font-family: 'Courier New', monospace;
        font-weight: 700;
        color: var(--share-title-color);
    }
}

// Webhook unavailable state
.webhook-unavailable {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 24px 16px;
    gap: 4px;
}

.unavailable-title {
    font-family: 'Avenir', sans-serif;
    font-weight: 700;
    font-size: 15px;
    color: var(--share-text);
}

.unavailable-desc {
    font-family: 'Avenir', sans-serif;
    font-size: 13px;
    color: var(--share-muted);
    line-height: 1.4;
    max-width: 320px;
}

.webhook-loading {
    display: flex;
    justify-content: center;
    padding: 24px;
}

.webhook-note {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    font-family: 'Avenir', sans-serif;
    font-size: 12px;
    color: var(--share-border);
    line-height: 1.4;
}

// Responsive
@media (max-width: 600px) {
    .tournament-share {
        width: 100%;
        height: 100%;
        padding: 16px;
        border-radius: 0 !important;
    }

    .share-title {
        font-size: 22px;
        line-height: 30px;
    }

    .tab-option {
        font-size: 14px;
    }

    .share-subtitle {
        font-size: 16px;
    }

    .social-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }

    .link-text {
        font-size: 14px;
    }

    .embed-code-text {
        font-size: 13px;
    }

    .webhook-fields-grid {
        grid-template-columns: 1fr;
    }
}
</style>

<!-- Unscoped dark theme overrides -->
<style lang="scss">
.theme--dark .tournament-share {
    --share-bg: #1e1e1e;
    --share-text: #e0e0e0;
    --share-title-color: #f5f5f5;
    --share-border: #555;
    --share-input-border: #777;
    --share-tab-bg: #2c2c2c;
    --share-chip-bg: #333;
    --share-muted: #999;
    --share-hover: rgba(255, 255, 255, 0.04);
    --share-icon-color: #ccc;
}
</style>
